import { v4 as uuidv4 } from 'uuid'
import {
    BundleCartItemDto,
    CartItemTicketDetailDto,
    CreateCartItemCommand,
    GetCartItemPresenter,
    ProductBundlePresenter,
    TicketDto,
    useCreateBundleCartItemsMutation,
    useCreateCartItemsMutation,
    useDeleteCartItemsByIdMutation,
} from 'store/api-slice/hooks'
import { Person, Ticket } from 'store/api-slice/types'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import {
    addTicket,
    removeAllTickets,
    removeTicketTypes,
    removeTicketsKeepDate,
    setFamilyPackageSelection,
    setFamilyPackageVisibility,
    setIsPackageConflictModalOpened,
    setIsSeniorConflictModalOpen,
    setTicketsUpdating,
    setTicketsVisible,
} from 'store/ticket-slice'
import { useCart } from 'store/cart-slice/hooks'
import { useEffect, useMemo } from 'react'
import { setIsErrorModalOpened } from 'store/general-slice'
import { handleError } from 'helpers/errors'
import { FAMILY_PACKAGE_PERSON_COUNT } from 'components/global/Family/constants'
import { PRODUCT_TYPE_TICKET } from 'constants/app'
import {
    sumTicketsBundlesDates,
    sumTicketsDates,
} from 'components/global/Summary/helpers'

interface AddTicketProps {
    ticket: TicketDto
    person: Person
    cartItemId?: string
}

export const useTickets = (
    boxId?: string
): {
    isTicketInCart: boolean
    isTicketProcessing: boolean
    ticketsDates: string[]
    addTicketToCart: ({
        ticket,
        person,
        cartItemId,
    }: AddTicketProps) => Promise<void>
    removeTicketFromCart: (isWithoutCartReload?: boolean) => Promise<void>
    isFamilyPackageProcessing: boolean
    hasFamilyPackageSucceeded: boolean
    isFamilyPackageInCart: boolean
    addFamilyPackageToCart: (
        productBundles: ProductBundlePresenter[] | undefined
    ) => Promise<void>
    removeFamilyPackageFromCart: () => Promise<void>
    addSeniorPackageToCart: (
        productBundles: ProductBundlePresenter[] | undefined
    ) => Promise<void>
    removeAllTicketsFromCart: (
        shouldLeavePreconfigured?: boolean
    ) => Promise<boolean | undefined>
} => {
    const dispatch = useAppDispatch()

    const { people } = useAppSelector((state) => state.ticket)
    const {
        tickets: cartTickets,
        ticketsBundles,
        marchVouchers,
    } = useAppSelector((state) => state.cart.items)
    const { language } = useApp()
    const { refetchCart, cartCreatorType } = useCart()
    const [deleteCartItems, deleteStatus] = useDeleteCartItemsByIdMutation()
    const [postCartItems, postStatus] = useCreateCartItemsMutation()
    const [postCartFamilyBundleItems, postFamilyBundleStatus] =
        useCreateBundleCartItemsMutation()
    const [postCartSeniorBundleItems, postSeniorBundleStatus] =
        useCreateBundleCartItemsMutation()

    const isTicketProcessing =
        deleteStatus.isLoading ||
        postStatus.isLoading ||
        postSeniorBundleStatus.isLoading
    const isFamilyPackageProcessing =
        postFamilyBundleStatus.isLoading || deleteStatus.isLoading

    const hasFamilyPackageSucceeded = postFamilyBundleStatus.isSuccess

    const isTicketInCart = cartTickets.some(
        (ticket) => ticket.detail.boxId === boxId
    )

    const isFamilyPackageInCart = !!ticketsBundles.length

    const bundlesItemsIds = useMemo(() => {
        const bundlesItems: GetCartItemPresenter[] = ticketsBundles
            .map(({ itemsBundle }: GetCartItemPresenter) => itemsBundle || [])
            .flat()
        const itemsIds: string[] = bundlesItems.map(
            ({ cartItemId }: GetCartItemPresenter) => cartItemId || ''
        )
        return itemsIds
    }, [ticketsBundles])

    const ticketsDates = cartTickets.length
        ? sumTicketsDates(cartTickets)
        : sumTicketsBundlesDates(ticketsBundles)

    useEffect(() => {
        dispatch(setTicketsUpdating(isTicketProcessing))
    }, [isTicketProcessing])

    const addTicketToCart = async ({
        ticket,
        person,
        cartItemId,
    }: AddTicketProps) => {
        const { title, time, zone, prices, productId } = ticket

        if (!productId || !prices || !boxId) return

        const ticketsToSend: CreateCartItemCommand[] = []
        const ticketsToSaveInRedux: Ticket[] = []

        prices.forEach((singleTicket) => {
            const { price, date } = singleTicket
            if (!title || !zone || !time || !date) return

            const updatedCartItemId = cartItemId || uuidv4()
            const productType = 'ticket'
            const quantity = 1
            const detail = {
                date,
                zone,
                person,
                ticketTime: time,
                boxId,
            }

            ticketsToSaveInRedux.push({
                cartItemId: updatedCartItemId,
                productId,
                productType,
                quantity,
                detail,
                name: title,
                unitPrice: price,
            })

            ticketsToSend.push({
                cartItemId: updatedCartItemId,
                productId,
                productType,
                quantity,
                detail,
            })
        })

        postCartItems({
            createCartItemsCommand: {
                items: ticketsToSend,
            },
            cartCreatorType,
            _locale: language,
        })
            .unwrap()
            .then(() => {
                ticketsToSaveInRedux.map((ticketToSave) =>
                    dispatch(
                        addTicket({
                            data: ticketToSave,
                            boxId,
                        })
                    )
                )

                refetchCart(true)
            })
            .catch(() => {
                dispatch(setIsErrorModalOpened(true))
            })
    }

    const removeTicketFromCart = async (isWithoutCartReload?: boolean) => {
        const boxedTickets = cartTickets.filter(
            ({ detail }) => detail.boxId === boxId
        )
        if (!boxedTickets.length) return

        const ticketsIdsToRemove = boxedTickets.map(
            ({ cartItemId }) => cartItemId || ''
        )
        const marchVouchersIdsToRemove = marchVouchers
            .map(({ cartItemId }) => cartItemId || '')
            .filter((id) => !!id)

        await deleteCartItems({
            _locale: language,
            deleteCartItemsByIdCommand: {
                cartItemUuids: [
                    ...ticketsIdsToRemove,
                    ...marchVouchersIdsToRemove,
                ],
            },
            cartCreatorType,
        })

        dispatch(removeTicketTypes(boxId || ''))

        if (!isWithoutCartReload) refetchCart()
    }

    interface IMapCartReadyBundles {
        productBundles: ProductBundlePresenter[]
        shouldGenerateNewBoxId?: boolean
    }

    const mapCartReadyBundles = ({
        productBundles,
        shouldGenerateNewBoxId = true,
    }: IMapCartReadyBundles): BundleCartItemDto[] => {
        const newBoxId = uuidv4()

        return productBundles.flatMap(({ productId, dates }) => {
            if (!productId || !dates) return []
            // add separate bundle for each day
            return dates?.map((date) => ({
                cartItemBundleId: uuidv4(),
                productId,
                boxId: shouldGenerateNewBoxId || !boxId ? newBoxId : boxId,
                date,
            }))
        })
    }

    const addFamilyPackageToCart = async (
        productBundles: ProductBundlePresenter[] | undefined
    ) => {
        if (!productBundles) {
            dispatch(setIsErrorModalOpened(true))
            return
        }

        const cartReadyBundles: BundleCartItemDto[] = mapCartReadyBundles({
            productBundles,
        })

        try {
            const response = await postCartFamilyBundleItems({
                _locale: language,
                cartCreatorType,
                createBundleCartItemsCommand: {
                    cartCreatorType,
                    bundles: [...cartReadyBundles],
                },
            })
            handleError(response)
        } catch (error) {
            const { message } = error as Error

            if (message.includes('Senior Offer Conflict')) {
                dispatch(setIsSeniorConflictModalOpen(true))
                throw error
            } else if (message?.includes('Conflict')) {
                dispatch(setIsPackageConflictModalOpened(true))
                throw error
            } else dispatch(setIsErrorModalOpened(true))
        } finally {
            refetchCart(true)
        }
    }

    const removeFamilyPackageFromCart = async () => {
        if (!ticketsBundles.length || !bundlesItemsIds.length) return

        try {
            const response = await deleteCartItems({
                _locale: language,
                deleteCartItemsByIdCommand: {
                    cartItemUuids: bundlesItemsIds,
                },
                cartCreatorType,
            })
            handleError(response)

            refetchCart()

            if (people === FAMILY_PACKAGE_PERSON_COUNT)
                dispatch(setTicketsVisible(false))

            dispatch(setFamilyPackageSelection({ isSelected: false }))
        } catch (error) {
            console.log(error)
        }
    }

    const addSeniorPackageToCart = async (
        productBundles: ProductBundlePresenter[] | undefined
    ) => {
        if (!productBundles || !boxId) {
            dispatch(setIsErrorModalOpened(true))
            return
        }

        const cartReadyBundles: BundleCartItemDto[] = mapCartReadyBundles({
            productBundles,
            shouldGenerateNewBoxId: false,
        })

        const ticketsToSaveInRedux: Ticket[] = productBundles
            .map((singleBundle) => {
                const { productId, dates, name, unitPrice } = singleBundle

                if (
                    !productId ||
                    !dates ||
                    !dates.length ||
                    !name ||
                    !unitPrice
                )
                    return {} as Ticket

                return dates.map((date, index) => {
                    const detail: CartItemTicketDetailDto = {
                        date,
                        zone: 'relax',
                        person: 'senior',
                        // TODO: fix/extend types when there is more time / with selfcare maybe
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        ticketTime: productId,
                        boxId,
                    }

                    return {
                        cartItemId: cartReadyBundles[index].cartItemBundleId,
                        cartItemBundleId:
                            cartReadyBundles[index].cartItemBundleId,
                        productId,
                        productType: PRODUCT_TYPE_TICKET,
                        quantity: 1,
                        detail,
                        name,
                        unitPrice,
                    }
                })
            })
            .flat()

        try {
            const response = await postCartSeniorBundleItems({
                _locale: language,
                cartCreatorType,
                createBundleCartItemsCommand: {
                    cartCreatorType,
                    bundles: [...cartReadyBundles],
                },
            })
            handleError(response)

            ticketsToSaveInRedux.map((ticketToSave) =>
                dispatch(
                    addTicket({
                        data: ticketToSave,
                        boxId,
                    })
                )
            )
        } catch (error) {
            const { message } = error as Error

            if (message.includes('Senior Offer Conflict')) {
                dispatch(setIsSeniorConflictModalOpen(true))
                throw error
            } else if (message?.includes('Conflict')) {
                dispatch(setIsPackageConflictModalOpened(true))
                throw error
            } else dispatch(setIsErrorModalOpened(true))
        } finally {
            refetchCart(true)
        }
    }

    const removeAllTicketsFromCart = async (
        shouldLeavePreconfigured = false
    ) => {
        if (!cartTickets.length && !bundlesItemsIds.length) {
            if (shouldLeavePreconfigured) {
                dispatch(removeTicketsKeepDate())
            } else {
                dispatch(removeAllTickets())
                dispatch(setFamilyPackageSelection({ isSelected: false }))
                dispatch(setFamilyPackageVisibility(false))
            }
            return
        }

        const ticketsIdsToRemove = cartTickets.map(
            (ticket) => ticket.cartItemId || ''
        )
        const bundlesIdsToRemove = [...bundlesItemsIds]
        const marchVouchersIdsToRemove = marchVouchers
            .map(({ cartItemId }) => cartItemId || '')
            .filter((id) => !!id)

        try {
            await deleteCartItems({
                _locale: language,
                deleteCartItemsByIdCommand: {
                    cartItemUuids: [
                        ...ticketsIdsToRemove,
                        ...bundlesIdsToRemove,
                        ...marchVouchersIdsToRemove,
                    ],
                },
                cartCreatorType,
            })

            if (shouldLeavePreconfigured) {
                dispatch(removeTicketsKeepDate())
            } else {
                dispatch(removeAllTickets())
                dispatch(setFamilyPackageSelection({ isSelected: false }))
                dispatch(setFamilyPackageVisibility(false))
            }

            refetchCart()
            return true
        } catch (error) {
            console.log(error)
        }
    }

    return {
        isTicketProcessing,
        isTicketInCart,
        addTicketToCart,
        removeTicketFromCart,
        isFamilyPackageProcessing,
        hasFamilyPackageSucceeded,
        isFamilyPackageInCart,
        addFamilyPackageToCart,
        addSeniorPackageToCart,
        removeFamilyPackageFromCart,
        removeAllTicketsFromCart,
        ticketsDates,
    }
}
